/**
 * Floe SDK - Main Export
 */

import { OnboardingSDK } from './onboarding-sdk';
import { createLogger } from './logger';
import type { FloeMcpConfig } from './types';
import type { ExitIntentConfig } from './exit-intent';

export type { FloeMcpConfig, FloeMcpBearerTokenProvider } from './types';
export type {
  ExitIntentConfig as FloeExitIntentConfig,
  ExitIntentOptions as FloeExitIntentOptions,
} from './exit-intent';

/**
 * User information for personalization
 */
export interface FloeUserInfo {
  phone?: string;
  externalId?: string;
  email?: string;
  name?: string;
  company?: string;
  designation?: string;
  metadata?: Record<string, any>;
}

/**
 * Nudge configuration for Intercom Fin-style sliding notification
 */
export interface FloeNudgeConfig {
  /** Main nudge message (default: "Hi 👋 How can I help you today?") */
  text?: string;
  /**
   * Auto-show the nudge when minimized.
   *
   * Default: true — EXCEPT under `activation: 'on_demand'` (support mode),
   * where it defaults to false. A nudge is a top-of-funnel affordance: on a
   * marketing site the agent should reach out, but an in-product support agent
   * that taps the user on the shoulder after every page load is exactly the
   * unprompted behaviour support mode removes. Set it explicitly to true to
   * get the bubble back in support mode.
   */
  autoShow?: boolean;
  /** Auto-hide after ms, 0 = never (default: 0) */
  autoHideDelay?: number;
}

/**
 * Floe SDK Configuration Options
 */
export interface FloeConfig {
  /** Your Floe API client key (required) */
  clientKey: string;
  /** Floe API URL (defaults to production) */
  apiUrl?: string;
  /** Enable demo mode — shows full-screen product video from AI agent */
  demoMode?: boolean;
  /**
   * Enable website-agent mode (FLO-320). Default: false.
   *
   * A top-of-funnel Q&A chat that runs on the SAME merged pipecat session as the
   * demo agent. When on, Floe renders a corner launcher (FLO-520: a video
   * tile when the avatar is enabled with configured media, else an "Ask {agent}"
   * pill), plus any page-scoped nudge chips as a row along the bottom; clicking
   * the launcher opens the chat and starts the voice session MUTED (mic off, bot audio
   * on — the user can instant-unmute or type) with NO email gate. The client
   * sends `entryPhase: "q_and_a"` in the /sites/start-session request so the
   * server enters the Q&A phase.
   *
   * Off by default, so existing demo / onboarding embeds are unaffected. Ignored
   * when demoMode is also set (demoMode takes precedence).
   */
  websiteAgent?: boolean;
  /**
   * FLO-514/519: how the website agent's open chat renders. Website-agent mode
   * only; ignored in demo/onboarding mode.
   *
   *   'floating' — (default) a centered chat card over a dimmed backdrop.
   *   'sidebar'  — a full-height rail docked to the right edge. At ≥1024px the
   *                host page is squeezed by `websiteAgentSidebarWidth` so no
   *                content hides under the rail, and there is NO backdrop — the
   *                page stays interactive beside the agent.
   *
   * Normally configured PER SITE in the Floe dashboard and delivered in the
   * resolved `websiteAgent` config; this init-time option is an override for
   * local testing / staged rollout and wins over the server value.
   */
  websiteAgentDisplayMode?: 'floating' | 'sidebar';
  /**
   * Rail width in px for `websiteAgentDisplayMode: 'sidebar'`. Clamped to
   * 320–480; defaults to 400 (Qualified parity). Same override precedence as
   * {@link websiteAgentDisplayMode}.
   */
  websiteAgentSidebarWidth?: number;
  /**
   * Silence, in milliseconds, after which the live avatar track is replaced by
   * the site's idle loop. Default 8000; `0` disables; values below 3000 are
   * clamped (see `sdk/avatar-idle.ts` for why the floor exists).
   *
   * Presentation only — it does not reduce what the video avatar costs.
   */
  avatarIdleAfterMs?: number;
  /** Site ID for demo mode (used to start demo session) */
  demoSiteId?: string;
  /** Enable screen sharing (default: true) */
  enableScreenCapture?: boolean;
  /** Enable voice interaction (default: true) */
  enableAudio?: boolean;
  /** User's industry for context */
  industry?: string;
  /** User's use case */
  useCase?: string;
  /** User's company name */
  companyName?: string;
  /** Company size */
  companySize?: string;
  /** User's role */
  role?: string;
  /** Enable debug logging */
  debug?: boolean;
  /** User identification for personalization */
  userInfo?: FloeUserInfo;
  /** Skip the welcome modal for returning users */
  skipOnboardingModal?: boolean;
  /** Nudge configuration - Intercom Fin-style sliding notification */
  nudge?: FloeNudgeConfig;
  /**
   * Surface the demo or website agent when a desktop visitor moves toward the
   * browser chrome. Off by default. `true` uses safe defaults; an object can
   * customize the minimum dwell, top-edge tolerance, and opening message.
   *
   * Exit intent only reveals dormant UI. It does NOT start a voice session,
   * request microphone access, or send a visitor message. The visitor's first
   * click/type remains the paid-session and microphone gate.
   *
   * Supported by `websiteAgent: true` and docked demo embeds. It is ignored by
   * onboarding/support agents and `embedMode: 'fullscreen'` demos.
   *
   * ```js
   * const floe = Floe({
   *   clientKey: 'your-key',
   *   websiteAgent: true,
   *   exitIntent: {
   *     message: 'Before you go — want to see it live?',
   *     minTimeOnPageMs: 5000,
   *   },
   * });
   * ```
   */
  exitIntent?: boolean | ExitIntentConfig;
  /**
   * FLO-585: where the website, onboarding, or support launcher docks, and how
   * far from the edge.
   *
   * Bottom-right is the default and also the most contested 80px on the web —
   * Intercom, Pendo, Appcues and Userflow all default there — so a customer
   * running one of them AND Floe currently gets two overlapping bubbles and no
   * way out but a CSS override against our z-index. Set `position: 'bottom-left'`
   * (optionally with an `inset` in px) to move Floe out of the way. The value is
   * an init-time config, so it survives every reload for free. Demo mode has no
   * closed launcher, so this setting has no effect there.
   *
   * The launcher face is the CUSTOMER's brand (site logo → letter-mark on the
   * site accent), same as the website agent's launcher and the open panel — there
   * is no Floe mark to configure and no paid tier to gate it.
   */
  launcher?: {
    position?: 'bottom-right' | 'bottom-left';
    inset?: number;
  };
  /**
   * Pre-supplied prospect email. When set, the SDK's built-in
   * DemoIntroForm skips its first "What's your work email?" stage and
   * starts at name + optional fields with this value pre-filled.
   *
   * The intended pattern is to collect the email on your own page
   * BEFORE calling Floe() — that gates the (paid) pipecat + avatar
   * session behind a real signal of intent. Prospects who land but
   * never enter an email don't trigger a backend session at all.
   *
   * Setting it does NOT start a session. It shrinks what the form asks
   * for; the prospect's submit is still what spawns pipecat, so a page
   * load on its own is never billed — including a page load by something
   * that isn't a person.
   */
  prospectEmail?: string;
  /**
   * How the ONBOARDING agent activates (FLO-534). Default: 'proactive'.
   *
   *   'proactive' — (default) the collapsed launcher auto-shows an onboarding
   *                 nudge for an eligible user. A session connects only after a
   *                 click, then opens by offering to get them set up.
   *   'on_demand' — "support mode". The launcher defaults to no automatic
   *                 nudge, but `nudge.autoShow: true` can opt back in. The agent
   *                 never spends a session on its own. It connects only when
   *                 the user clicks it, then opens reactively ("what can I help
   *                 with?") instead of pitching a walkthrough. It can still
   *                 run one — the user asking is the trigger.
   *
   * The two are the same agent on the same surface (your product), differing
   * in opening posture. A product that wants both — guided setup for brand-new
   * accounts, on-demand help for everyone after — picks per user at init:
   *
   *     Floe({ clientKey, activation: user.isNewlySignedUp ? 'proactive' : 'on_demand' })
   *
   * Ignored by the demo and website agents. Those live on the marketing site,
   * already defer their connect to a user gesture, and DO nudge on purpose.
   *
   * NOT the same as `skipOnboardingModal`. That one answers "has this person
   * been here before?" and still leaves a proactive agent waiting behind the
   * launcher; this one answers "which opening posture should the agent use?"
   * and is enforced on the server too. They compose.
   */
  activation?: 'proactive' | 'on_demand';
  /**
   * Authenticated remote data tools for `activation: 'on_demand'` support.
   *
   * The SDK invokes `getBearerToken` only when the user clicks to start a
   * support session. It never reads localStorage itself, and it never sends
   * this config in proactive onboarding, demo, or website-agent sessions.
   * If the provider or config is unavailable, support chat still starts
   * normally without MCP tools.
   */
  mcp?: FloeMcpConfig;
  /**
   * Per-visitor consent gating for SDK-collected identity.
   *
   *   'not-required' — (default) today's behavior: the SDK mints a visitor
   *                    token and emits pre-interaction events on load.
   *   'pending'      — nothing identity-bearing happens until the host calls
   *                    `floe.consent('granted')`: no `floe_anonymous_id`
   *                    write, no visitor-token mint, no visitor/nudge event
   *                    queueing, no server-side conversation open. The widget
   *                    still renders, and a chat the visitor starts still
   *                    works — as a session without identity keys.
   *
   * `floe.consent('denied')` behaves like 'pending' forever (sticky for the
   * page load). Unset, the per-site default from the Floe dashboard applies
   * (`websiteAgent.consentModeDefault` via validate-client-key) when present.
   */
  consentMode?: 'not-required' | 'pending';
  /**
   * How the demo overlay renders when embedded (FLO-285). Demo mode only.
   *
   *   'docked'     — (default) start minimized as a bottom-docked pill that
   *                  captures a work email, then go full-bleed once the demo
   *                  starts. Lower-friction entry for marketing-site embeds;
   *                  the host page stays visible + interactive behind the pill.
   *   'fullscreen' — open the email intro form full-screen immediately, then
   *                  run the demo full-bleed. Use on dedicated demo pages
   *                  (e.g. /demo/[prospect], /d/[token]) where the whole page
   *                  IS the demo.
   *
   * ⚠️ BEHAVIOUR CHANGE: the default is 'docked'. Existing `Floe({ demoMode:
   * true })` embeds that relied on the old full-screen takeover must now pass
   * `embedMode: 'fullscreen'` explicitly to preserve it. Pin it once the new
   * SDK ships to your CDN — the old SDK ignored this option and always went
   * full-screen, so an un-pinned embed silently switches to the docked pill.
   */
  embedMode?: 'docked' | 'fullscreen';

  // ─── Per-link demo options (the /d/[token] launch shell sets these) ──────
  // All three are read by OnboardingSDK and declared on the internal SDKConfig,
  // but were missing HERE — and `convertFloeToSDKConfig` is an explicit
  // field-by-field map with no spread, so they were dropped at this boundary
  // and never reached the SDK. See the note on that function.
  /**
   * The DemoLink id (FLO-150). Forwarded into `POST /sites/demo/:siteId/start`,
   * which is the ONLY way floe-api learns which link a session came from — so
   * without it `getDemoConfig(siteId, demoLinkId)` resolves the SITE layer only
   * and EVERY per-link override (persona, discovery, identity capture) silently
   * falls back to the site default.
   */
  demoLinkId?: string;
  /** HTTPS booking URL override for demo and website agents. An explicit
   *  `{email}` query value opts into email prefill. Falls back to the site's
   *  `calBookingUrl` from validate-client-key when unset. */
  demoCalendarLink?: string;
  /**
   * WHEN the demo asks who the prospect is, already resolved (site ← link) by
   * floe-api. Overrides the site-level policy the SDK fetches for itself.
   * Undefined = use the site policy; an unrecognized value is rejected by
   * `parseDemoCapturePolicy` and reads as today's gating intro form.
   */
  demoIdentityCapture?: import('../components/demoLayout').DemoCapturePolicy;
}



/**
 * The FloeConfig -> SDKConfig map lives in its own module so it can be unit
 * tested. Importing `index.ts` pulls in OnboardingSDK and the whole React UI
 * tree (WebsiteAgentChat calls `DOMPurify(window)` at module scope), which
 * needs a DOM the `node` test environment doesn't have — so for as long as this
 * function lived here, the boundary that silently drops unmapped options was
 * the one piece of the SDK that could not be pinned by a test.
 */
import { convertFloeToSDKConfig } from './floe-config';
export { convertFloeToSDKConfig };

/**
 * Extended SDK instance with ready promise
 */
export interface FloeSDKInstance extends OnboardingSDK {
  /** Promise that resolves when SDK is fully initialized */
  ready: Promise<void>;
}

/**
 * Initialize Floe SDK
 * @param config Configuration options
 * @returns SDK instance with a `ready` promise for initialization status
 * 
 * @example
 * // Fire and forget (errors logged to console)
 * const sdk = Floe({ clientKey: 'your-key' });
 * 
 * @example
 * // Await initialization
 * const sdk = Floe({ clientKey: 'your-key' });
 * await sdk.ready;
 * console.log('SDK initialized!');
 * 
 * @example
 * // Handle initialization errors
 * const sdk = Floe({ clientKey: 'your-key' });
 * sdk.ready.catch(err => console.error('Init failed:', err));
 */
export function Floe(config: FloeConfig): FloeSDKInstance {
  // Validate required fields
  if (!config.clientKey) {
    throw new Error('[Floe] clientKey is required');
  }

  // Convert to internal SDK config with proper defaults
  const sdkConfig = convertFloeToSDKConfig(config);

  // Build a per-instance logger so multiple Floe() calls don't interfere with each other.
  const instanceLogger = createLogger(sdkConfig.debug ?? false);

  const sdk = new OnboardingSDK(sdkConfig, instanceLogger) as FloeSDKInstance;

  // Start initialization and expose the promise
  sdk.ready = sdk.init().catch((error) => {
    instanceLogger.error('[Floe] Initialization failed:', error);
    throw error;
  });
  
  return sdk;
}

// Export for browser/CDN usage
if (typeof window !== 'undefined') {
  (window as any).Floe = Floe;
}
